// Workspace UI catalog — the assistant's THIRD knowledge layer (generic →
// DOMQL/platform → THIS workspace). A compact, per-turn snapshot of the active
// workspace's personalized surface, sent as dispatchContext.uiCatalog and
// rendered into the system instructions server-side
// (ConversationOrchestratorService.buildWorkspaceCatalogLine): the module's
// live routes (open_page_in_split targets), its component vocabulary
// (render_answer_ui `extends` targets), its loaded root state keys with sampled
// row shapes (live data the AI reads via `s.root.<key>` — no fetch round-trip),
// and the workspace design-system accent vars. Personalization is server-driven
// (workspace.settings.workspaceModule → loadWorkspaceProject) — this only
// REFLECTS what that mechanism injected, so a workspace without a module (e.g.
// aihouse today) still gets a truthful minimal catalog and everything degrades
// cleanly.
//
// Size discipline: the whole catalog must stay well under the server's 5K cap —
// names and shapes only, never data rows beyond one sampled row's keys.

import { resolveWorkspaceConfig, resolveDesignSystemVars } from './workspaceConfigs.js'
import { GENERATED_APP_COMPONENT_ALLOWLIST } from './generatedAppRules.js'
import { designSystemNames } from './generatedViewStyleLint.js'

const MAX_LIST = 24
const MAX_STATE_KEYS = 12
// The AI-callable verbs are the CALL GATE's list, not a sample: the server
// offers a module verb only when the shell reports it live, so a verb past a
// cut is simply not offered. 24 was one app's worth — Storm's three apps
// declare 24 between them, and the action verb appended after them fell out
// (MODULE-ACTIONS-PROPOSAL-NEVER-OFFERED-1). Names are short; the server's
// line cap (5K) bounds the instruction text either way.
const MAX_AI_FNS = 96
const MAX_ACTIONS = 48
const PROPOSE_FN = 'proposeModuleAction'

// One row's shape — key names plus primitive example values (strings clipped),
// enough for the model to write correct filters without shipping the dataset.
//
// Non-primitive fields are described by a TYPE TOKEN, never by an elided
// value-shaped placeholder. This is load-bearing: the catalog is JSON.stringify'd
// straight into the system instruction, so whatever stands in for a nested
// object is read by the model as that field's example VALUE. The former `'{…}'`
// placeholder taught it that a nested payload field literally contains the
// two-character string `{…}`, and it dutifully echoed `"payload": "{…}"` back in
// tool calls (natali QA 2026-07-27). `<object>` / `<array:3>` read as
// annotations, carry the same information, and cannot be mistaken for data.
const _rowShape = (row) => {
  if (!row || typeof row !== 'object') return null
  const shape = {}
  for (const [key, value] of Object.entries(row).slice(0, 14)) {
    if (value == null) shape[key] = null
    else if (typeof value === 'string') shape[key] = value.slice(0, 24)
    else if (typeof value === 'number' || typeof value === 'boolean') shape[key] = value
    else if (Array.isArray(value)) shape[key] = `<array:${value.length}>`
    else shape[key] = `<object:${Object.keys(value).slice(0, 6).join(',')}>`
  }
  return shape
}

// `opts.designSystem` — the LIVE design system of the element the assistant's
// views mount under (its context's), the one render_answer_ui's lint judges
// colours and themes against (UI-CATALOG-CARRIES-NO-DESIGN-SYSTEM-NAMES-1).
export function buildUiCatalog(root, opts = {}) {
  if (!root) return null
  const project = root.workspaceProject
  const catalog = {}
  // The colour and theme names a generated view may use — FIRST in the
  // catalog, ahead of the long per-app lists.
  try {
    const ds = opts && opts.designSystem
    if (ds && typeof ds === 'object') {
      const names = designSystemNames(ds)
      if (names.colors.length || names.themes.length) catalog.designNames = names
    }
  } catch (_) {
    /* names are optional context — never block a turn on them */
  }

  if (project && typeof project === 'object') {
    if (project.id) catalog.module = project.id
    // WS-MODULE-STATE-SCOPE-1 — a module's rows live on ITS OWN state root now,
    // not on the shell root. Reading `root[key]` here would report every
    // module slice as `{ empty: true }` / `notLoaded` while the module has the
    // data, and the assistant would then re-trigger a loader that already ran.
    // The module store falls back to the shell root for anything it does not
    // own, so this read is correct for both scoped and unscoped modules.
    const modState = (project.id != null && root._moduleStateGet?.(project.id)) || root
    const pages = Object.keys(project.pages || {})
    if (pages.length) catalog.pages = pages.slice(0, MAX_LIST)
    // Only the components a generated view may EXTEND: the containment gate
    // (generatedAppContainment.js) mounts its reviewed list and refuses every
    // other name with component_not_allowed. An installed app's own
    // components are never on it, so advertising them taught the assistant to
    // build every answer view on them — Storm on dev 2026-10-04: six of ten
    // render_answer_ui calls refused for ValCard / ValCellMid
    // (MODULE-COMPONENTS-ADVERTISED-BUT-REFUSED-1).
    const reviewed = new Set(GENERATED_APP_COMPONENT_ALLOWLIST)
    const components = (Array.isArray(project.components) ? project.components : []).filter((n) =>
      reviewed.has(n)
    )
    if (components.length) catalog.components = components.slice(0, MAX_LIST)
    const widgets = Array.isArray(project.widgets)
      ? project.widgets.map((w) => w && w.id).filter(Boolean)
      : []
    if (widgets.length) catalog.widgets = widgets.slice(0, MAX_LIST)

    // The verbs this module lets the AI drive. Without these the assistant was
    // never told they exist: the shell GATES module fns at call time
    // (simoneAiTransport._moduleAiFns) but nothing advertised them, so a model
    // asked to draw on the Excalidraw board had no way to know
    // `excalidrawAddElements` was there — it could only offer to describe a
    // diagram in prose. Gating and advertising are different jobs and only one
    // of them was done.
    //
    // `docs` carries the call shape when the manifest supplies one, because
    // knowing a verb exists isn't enough to use it well: told only the name, an
    // assistant guesses the arguments, and on excalidraw that guess produced
    // free-floating text elements overlapping their boxes instead of bound,
    // wrapped labels.
    const aiFns = Array.isArray(project.aiFunctions) ? project.aiFunctions : []
    if (aiFns.length) {
      const listed = aiFns.slice(0, MAX_AI_FNS)
      // The approval-gated propose verb is never the one a cut drops.
      if (aiFns.includes(PROPOSE_FN) && !listed.includes(PROPOSE_FN)) {
        listed[listed.length - 1] = PROPOSE_FN
      }
      catalog.aiFunctions = listed
      const docs = project.aiFunctionDocs
      if (docs && typeof docs === 'object') {
        const named = {}
        for (const name of catalog.aiFunctions) {
          if (docs[name]) named[name] = docs[name]
        }
        if (Object.keys(named).length) catalog.aiFunctionDocs = named
      }
      // The listed verbs this shell runs READ-ONLY (MODULE-FN-READONLY-
      // ENFORCED-1): the server marks a module_fn tool read-only only when its
      // grant agrees, and the gate then runs it without the Run / Skip row on
      // a caller that refuses every write.
      const readOnly = Array.isArray(project.readOnlyAiFunctions)
        ? project.readOnlyAiFunctions.filter((name) => catalog.aiFunctions.includes(name))
        : []
      if (readOnly.length) catalog.readOnlyAiFunctions = readOnly
    }

    // The approval-gated ACTIONS the installed apps declare (manifest
    // `actions`), live in this shell: the keys the assistant may propose with
    // propose_app_action — the member approves the card before anything runs.
    // Reported only while the propose verb itself is callable.
    const actionKeys = Array.isArray(project.actionKeys) ? project.actionKeys : []
    if (actionKeys.length && (catalog.aiFunctions || []).includes(PROPOSE_FN)) {
      catalog.moduleActions = actionKeys.slice(0, MAX_ACTIONS)
      const docs = project.actionDocs
      if (docs && typeof docs === 'object') {
        const named = {}
        for (const key of catalog.moduleActions) {
          if (typeof docs[key] === 'string' && docs[key]) named[key] = docs[key]
        }
        if (Object.keys(named).length) catalog.moduleActionDocs = named
      }
    }

    const stateKeys = Array.isArray(project.stateKeys) ? project.stateKeys : []
    if (stateKeys.length) {
      const keys = {}
      for (const key of stateKeys.slice(0, MAX_STATE_KEYS)) {
        const value = modState[key]
        if (Array.isArray(value)) {
          keys[key] = { count: value.length, row: _rowShape(value[0]) }
        } else if (value && typeof value === 'object') {
          keys[key] = { keys: Object.keys(value).slice(0, 10) }
        } else {
          keys[key] = { empty: true }
        }
      }
      catalog.stateKeys = keys
    }

    // Loader-backed module data — the imperative data paths auto-discovered
    // from the module's functions (workspaceProjects._discoverLoaders). Each
    // entry names the el.call loader, its primary root state key, and that
    // key's LIVE contents (count + row shape once loaded), so a drawn view
    // knows both where the data lands and whether it must trigger the load.
    const loaders = project.loaders && typeof project.loaders === 'object' ? project.loaders : null
    if (loaders) {
      const out = {}
      for (const [name, meta] of Object.entries(loaders).slice(0, 10)) {
        const key = Array.isArray(meta?.writes) ? meta.writes[0] : null
        if (!key) continue
        const value = modState[key]
        const entry =
          Array.isArray(value) && value.length
            ? { key, count: value.length, row: _rowShape(value[0]) }
            : { key, count: 0, notLoaded: true }
        if (Array.isArray(meta.flags) && meta.flags.length) entry.flags = meta.flags
        out[name] = entry
      }
      if (Object.keys(out).length) catalog.loaders = out
    }

    // Declared data recipes — declarative fetch descriptors the module ships
    // for AI reuse. Compact projection only (5K-cap discipline).
    const dataRecipes = Array.isArray(project.dataRecipes) ? project.dataRecipes : []
    if (dataRecipes.length) {
      catalog.dataRecipes = dataRecipes.slice(0, 8).map((r) => ({
        id: r.id || r.from,
        from: r.from,
        method: r.method || 'select',
        ...(r.params ? { params: r.params } : {}),
        ...(r.as ? { as: r.as } : {}),
        ...(r.describe ? { describe: String(r.describe).slice(0, 100) } : {})
      }))
    }
  }

  // Workspace skin accent — lets composed views echo the workspace's palette
  // (the injected components already carry it; this covers bespoke bits).
  try {
    const ds = resolveWorkspaceConfig(root)?.designSystem
    // Resolved for the theme on screen: a record may carry `varsLight`/
    // `varsDark`, or a `{ light, dark }` pair per token, and only one half of
    // each is in force (FW-DESIGN-SYSTEM-RECORD-NEEDS-PER-THEME-VARS-1). The
    // raw bag would hand the assistant both halves, or an object where a
    // colour belongs.
    const theme = globalThis.document?.documentElement?.getAttribute?.('data-theme')
    const vars = resolveDesignSystemVars(ds, theme)
    if (Object.keys(vars).length) {
      catalog.designSystemVars = Object.fromEntries(Object.entries(vars).slice(0, 10))
    }
  } catch (_) {
    /* skin is optional context — never block a turn on it */
  }

  return Object.keys(catalog).length ? catalog : null
}
